import React from 'react';
import { Grid, Card, CardContent, Typography, Box, Chip } from '@mui/material';
import {
  AccountBalance, TrendingUp, TrendingDown, HourglassEmpty,
  CheckCircle, AttachMoney
} from '@mui/icons-material';
import { brand, status, text, border } from '../../theme/colors';

/**
 * Payment Stats Cards
 * Modern gradient cards showing key payment metrics
 */
const PaymentStatsCards = ({ stats, onCardClick }) => {
  const cards = [
    {
      id: 'total_escrow',
      title: 'Total in Wallet',
      value: `$${(stats?.totalEscrow || 0).toLocaleString()}`,
      subtitle: `$${(stats?.totalFunded || 0).toLocaleString()} funded`,
      icon: <AccountBalance sx={{ fontSize: 40 }} />,
      color: brand.primary,
      trend: stats?.escrowTrend || 0,
      trendLabel: 'vs last month'
    },
    {
      id: 'available_balance',
      title: 'Available Balance',
      value: `$${(stats?.availableBalance || 0).toLocaleString()}`,
      subtitle: `${stats?.availablePercentage || 0}% of total`,
      icon: <AttachMoney sx={{ fontSize: 40 }} />,
      color: status.success,
      trend: stats?.balanceTrend || 0,
      trendLabel: 'ready to release'
    },
    {
      id: 'pending_releases',
      title: 'Pending Releases',
      value: `$${(stats?.pendingReleases || 0).toLocaleString()}`,
      subtitle: `${stats?.pendingCount || 0} payments`,
      icon: <HourglassEmpty sx={{ fontSize: 40 }} />,
      color: status.warning,
      trend: stats?.pendingTrend || 0,
      trendLabel: 'awaiting approval'
    },
    {
      id: 'completed',
      title: 'Completed Payments',
      value: `$${(stats?.completedPayments || 0).toLocaleString()}`,
      subtitle: `${stats?.completedCount || 0} transactions`,
      icon: <CheckCircle sx={{ fontSize: 40 }} />,
      color: '#16a085',
      trend: stats?.completedTrend || 0,
      trendLabel: 'this month'
    }
  ];

  const handleCardClick = (cardId) => {
    if (onCardClick) {
      onCardClick(cardId);
    }
  };

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      {cards.map((card) => (
        <Grid item xs={12} sm={6} lg={3} key={card.id}>
          <Card
            onClick={() => handleCardClick(card.id)}
            sx={{
              backgroundColor: 'white',
              border: `2px solid ${border.light}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              borderRadius: 3,
              cursor: 'pointer',
              transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ color: card.color }}>
                  {card.icon}
                </Box>
                {card.trend !== 0 && (
                  <Chip
                    icon={card.trend > 0 ? <TrendingUp /> : <TrendingDown />}
                    label={`${card.trend > 0 ? '+' : ''}${card.trend}%`}
                    size="small"
                    sx={{
                      bgcolor: `${card.color}20`,
                      color: card.color,
                      fontWeight: 600
                    }}
                  />
                )}
              </Box>

              <Typography variant="body2" sx={{ color: card.color, fontWeight: 600, mb: 0.5 }}>
                {card.title}
              </Typography>

              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: text.primary }}>
                {card.value}
              </Typography>

              <Typography variant="caption" sx={{ color: text.secondary }}>
                {card.subtitle}
              </Typography>

              {card.trendLabel && (
                <Typography variant="caption" sx={{ display: 'block', color: text.secondary, mt: 0.5 }}>
                  {card.trendLabel}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default PaymentStatsCards;
